import { useParseMeta } from "@2u-landing-pages/providers";
import { ContentfulImageConfig } from "@2u-landing-pages/types";
import { renderEmbeddedEntry, hyperLinkRenderer, renderMark, renderText } from "@2u-landing-pages/utils/contentful-richtext";
import { documentToReactComponents } from "@contentful/rich-text-react-renderer";
import { BLOCKS, INLINES } from "@contentful/rich-text-types";
import { Card as BricksCard } from "@red-digital/bricks";
import cn from "classnames";
import { forwardRef } from "react";
import { ContentfulImage } from "../../../ContentfulImage";
import { Initials } from "../../../Initials";
import { CardVariantProps } from "../../Card.type";
// @ts-ignore
import cardStyles from "../../Card.module.scss";
// @ts-ignore
import largeCardStyles from "./LargeCard.module.scss";

export const LargeCard = forwardRef<HTMLDivElement, CardVariantProps>(({
  entry,
  heading,
  subhead,
  body,
  className,
  style,
  phoneNumber,
  inspector,
}, ref) => {
  const parseMetadata = useParseMeta();
  const {
    initials,
    image,
    image_config,
    heading: contentfulHeading,
    subhead: contentfulSubhead,
    body: contentfulBody,
  } = entry.fields;

  const imageConfig = image_config as unknown as ContentfulImageConfig;
  const desktopCfg = imageConfig?.default;
  const mobileCfg = imageConfig?.mobile;
  const isSvgImage = image?.fields?.file?.contentType === "image/svg+xml";

  return (
    <BricksCard
      ref={ref}
      style={style}
      className={cn(cardStyles.card, largeCardStyles.large, className, {
        [cardStyles.initials]: Boolean(initials),
        [cardStyles.solidOverlay]: [desktopCfg, mobileCfg].some((cfg) => cfg?.gradientType === "solid"),
        [cardStyles.gradientOverlay]: [desktopCfg, mobileCfg].some((cfg) => cfg?.gradientType === "gradient"),
      })}
      layout={{
        breakpoint: "lg",
      }}
      slots={{
        start: {
          component: !image && initials ? <Initials initials={initials} size="large" /> : undefined,
        },
      }}
      media={
        image?.fields?.file?.url
          ? {
              component: (
                <ContentfulImage
                  image={image}
                  config={image_config}
                  className={cn({ [cardStyles.svgImage]: isSvgImage })}
                  {...inspector({
                    entryId: entry.sys.id,
                    fieldId: "image",
                  })}
                />
              ),
              position: "beforeContent",
            }
          : undefined
      }
      content={{
        headingProps: {
          variant: "heading-xl",
          className: cn(cardStyles.heading, largeCardStyles.heading),
          ...heading,
          children: contentfulHeading
            ? documentToReactComponents(contentfulHeading, {
                renderMark,
                renderText: (text) => renderText(parseMetadata ? parseMetadata(text) : text, phoneNumber),
                renderNode: {
                  [BLOCKS.PARAGRAPH]: (node, children) => children,
                  [INLINES.EMBEDDED_ENTRY]: (node) =>
                    renderEmbeddedEntry(
                      node,
                      "Card Large Heading",
                      contentfulHeading,
                      phoneNumber,
                      parseMetadata,
                      "md"
                    ),
                },
              })
            : undefined,
          ...inspector({
            entryId: entry.sys.id,
            fieldId: "icon",
          }),
        },
        subheadProps:
          !image && initials && contentfulSubhead
            ? {
                variant: "subhead-lg",
                modifier: 500,
                className: cardStyles.subhead,
                ...subhead,
                children: documentToReactComponents(contentfulSubhead, {
                  renderMark,
                  renderText: (text) => renderText(parseMetadata ? parseMetadata(text) : text, phoneNumber),
                  renderNode: {
                    [BLOCKS.PARAGRAPH]: (node, children) => children,
                    [INLINES.EMBEDDED_ENTRY]: (node) =>
                      renderEmbeddedEntry(node, "Card Large Subhead", contentfulSubhead, phoneNumber, parseMetadata),
                  },
                }),
              }
            : undefined,
        descriptionProps: {
          modifier: 500,
          variant: "body-lg",
          className: cn(cardStyles.body, largeCardStyles.body),
          ...body,
          children: contentfulBody
            ? documentToReactComponents(contentfulBody, {
                renderMark,
                renderText: (text) => renderText(parseMetadata ? parseMetadata(text) : text, phoneNumber),
                renderNode: {
                  [BLOCKS.PARAGRAPH]: (node, children) => children,
                  [INLINES.EMBEDDED_ENTRY]: (node) =>
                    renderEmbeddedEntry(node, "Card Large Body", contentfulBody, phoneNumber, parseMetadata),
                  [INLINES.HYPERLINK]: (node) => hyperLinkRenderer(node, "Card Large - Body"),
                },
              })
            : undefined,
          ...inspector({
            entryId: entry.sys.id,
            fieldId: "body",
          }),
        },
      }}
    />
  );
});

LargeCard.displayName = "LargeCard";